import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart, ArrowLeft, CreditCard, DollarSign, CheckCircle, AlertCircle, ExternalLink
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const presetAmounts = [25, 50, 100, 250, 500, 1000];

const Donate = () => {
  const { language } = useLanguage();
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [donorInfo, setDonorInfo] = useState({ first_name: '', last_name: '', email: '' });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const t = language === 'en' ? {
    title: "Make a Donation",
    subtitle: "Your generous contribution helps survivors of abuse, violence, and trauma find healing and hope.",
    impact: "Your Impact",
    impactItems: [
      { amt: "$25", desc: "Provides counseling resources for one survivor" },
      { amt: "$100", desc: "Funds a workshop session on healing and recovery" },
      { amt: "$500", desc: "Sponsors a survivor's full conference attendance" },
      { amt: "$1,000", desc: "Supports our community outreach programs" },
    ],
    selectAmount: "Select Donation Amount (USD)",
    custom: "Custom Amount",
    customPlaceholder: "Enter amount in USD",
    yourInfo: "Your Information",
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email Address",
    messageLabel: "Leave a Message (Optional)",
    messagePlaceholder: "Your words of encouragement for survivors...",
    donate: "Donate Now",
    donateStripe: "Donate with Stripe (International)",
    donateHint: "Use Stripe if you are outside North America.",
    processing: "Processing...",
    secureNote: "Secure payment. You will be redirected to complete your donation.",
    thankYou: "Thank You for Your Generosity!",
    thankYouMsg: "You will be redirected to complete your donation securely.",
    back: "Back to Home",
    currency: "USD",
  } : {
    title: "Faire un Don",
    subtitle: "Votre contribution généreuse aide les survivantes d'abus, de violence et de traumatisme a trouver la guérison et l'espoir.",
    impact: "Votre impact",
    impactItems: [
      { amt: "25 $", desc: "Fournit des ressources de conseil pour une survivante" },
      { amt: "100 $", desc: "Finance une session d'atelier sur la guerison" },
      { amt: "500 $", desc: "Parraine la participation complète d'une survivante" },
      { amt: "1 000 $", desc: "Soutient nos programmes communautaires" },
    ],
    selectAmount: "Choisir le Montant du Don (USD)",
    custom: "Montant Personnalisé",
    customPlaceholder: "Entrer le montant en USD",
    yourInfo: "Vos informations",
    firstName: "Prenom",
    lastName: "Nom",
    email: "Adresse courriel",
    messageLabel: "Laisser un Message (Optionnel)",
    messagePlaceholder: "Vos mots d'encouragement pour les survivantes...",
    donate: "Faire un Don",
    donateStripe: "Faire un Don par Stripe (International)",
    donateHint: "Utilisez Stripe si vous êtes hors d'Amérique du Nord.",
    processing: "Traitement...",
    secureNote: "Paiement sécurisé. Vous serez redirigé pour compléter votre don.",
    thankYou: "Merci pour Votre Générosité!",
    thankYouMsg: "Vous serez redirigé pour compléter votre don en toute sécurité.",
    back: "Retour a l'Accueil",
    currency: "USD",
  };

  const selectedAmount = customAmount ? parseInt(customAmount) : (amount ? parseInt(amount) : 0);

  const startDonation = async (provider) => {
    if (!selectedAmount || selectedAmount < 1) {
      setError(language === 'fr' ? 'Veuillez sélectionner un montant.' : 'Please select an amount.');
      return;
    }
    if (!donorInfo.first_name || !donorInfo.last_name || !donorInfo.email) {
      setError(language === 'fr' ? 'Veuillez remplir vos informations.' : 'Please fill in your information.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const endpoint = provider === 'stripe' ? '/api/donate/stripe/create-session' : '/api/donate/create-link';
      const payload = {
        amount_usd: selectedAmount,
        first_name: donorInfo.first_name,
        last_name: donorInfo.last_name,
        email: donorInfo.email,
        message: message,
      };
      if (provider === 'stripe') payload.origin_url = window.location.origin;
      const response = await axios.post(`${BACKEND_URL}${endpoint}`, payload);
      if (response.data.checkout_url) {
        setSuccess(true);
        setTimeout(() => { window.location.href = response.data.checkout_url; }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    startDonation('stripe');
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex flex-col">
        <div className="bg-[#1D1D1D] py-3 px-4">
          <div className="max-w-5xl mx-auto">
            <Link to="/">
              <img src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg" alt="Logo" className="h-10" />
            </Link>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h2 className="text-2xl font-extrabold text-[#1D1D1D] mb-3">{t.thankYou}</h2>
            <p className="text-gray-500 text-sm mb-2">{t.thankYouMsg}</p>
            <p className="text-[#CD0000] font-extrabold text-3xl mb-6">${selectedAmount} {t.currency}</p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#CD0000] mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Header */}
      <div className="bg-[#1D1D1D] py-3 px-4">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg" alt="Logo" className="h-10" />
            <span className="text-white/60 text-xs font-bold tracking-widest hidden sm:inline">TALITHA KOUMI TUG</span>
          </Link>
          <Link to="/" data-testid="donate-back-link" className="text-white/60 hover:text-white flex items-center gap-1 text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t.back}
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left: Impact sidebar */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-6 h-6 text-[#CD0000]" />
                <h1 className="text-2xl font-extrabold text-[#1D1D1D]">{t.title}</h1>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{t.subtitle}</p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-extrabold text-[#1D1D1D] mb-4">{t.impact}</h3>
              <div className="space-y-4">
                {t.impactItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-14 shrink-0 text-center bg-[#CD0000]/10 text-[#CD0000] font-extrabold text-sm py-2 rounded-lg">
                      {item.amt}
                    </div>
                    <p className="text-gray-600 text-xs leading-relaxed pt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Donation form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} data-testid="donation-form" className="space-y-6">
              {/* Amount Selection */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-extrabold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                  {t.selectAmount}
                </h3>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {presetAmounts.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => { setAmount(String(amt)); setCustomAmount(''); }}
                      data-testid={`donate-amount-${amt}`}
                      className={`py-3 rounded-xl font-extrabold text-sm transition-all border-2 ${
                        amount === String(amt) && !customAmount
                          ? 'bg-[#CD0000] text-white border-[#CD0000]'
                          : 'bg-white text-[#1D1D1D] border-gray-200 hover:border-[#CD0000]/50'
                      }`}
                    >
                      ${amt}
                    </button>
                  ))}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1D1D1D] mb-1.5">{t.custom}</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
                    <input
                      type="number"
                      min="1"
                      data-testid="donate-custom-amount"
                      value={customAmount}
                      onChange={(e) => { setCustomAmount(e.target.value); setAmount(''); }}
                      placeholder={t.customPlaceholder}
                      className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#CD0000] focus:border-transparent"
                    />
                  </div>
                </div>
                {selectedAmount > 0 && (
                  <div className="mt-4 text-center">
                    <span className="text-[#CD0000] font-extrabold text-3xl">${selectedAmount}</span>
                    <span className="text-gray-400 text-sm ml-2">{t.currency}</span>
                  </div>
                )}
              </div>

              {/* Donor Info */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-extrabold text-[#1D1D1D] mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#D4AF37]" />
                  {t.yourInfo}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#1D1D1D] mb-1.5">{t.firstName} <span className="text-[#CD0000]">*</span></label>
                    <input
                      type="text"
                      data-testid="donate-first-name"
                      value={donorInfo.first_name}
                      onChange={(e) => setDonorInfo(p => ({...p, first_name: e.target.value}))}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1D1D1D] mb-1.5">{t.lastName} <span className="text-[#CD0000]">*</span></label>
                    <input
                      type="text"
                      data-testid="donate-last-name"
                      value={donorInfo.last_name}
                      onChange={(e) => setDonorInfo(p => ({...p, last_name: e.target.value}))}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#D4AF37]"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-[#1D1D1D] mb-1.5">{t.email} <span className="text-[#CD0000]">*</span></label>
                    <input
                      type="email"
                      data-testid="donate-email"
                      value={donorInfo.email}
                      onChange={(e) => setDonorInfo(p => ({...p, email: e.target.value}))}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#D4AF37]"
                    />
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <label className="block text-sm font-semibold text-[#1D1D1D] mb-1.5">{t.messageLabel}</label>
                <textarea
                  data-testid="donate-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder={t.messagePlaceholder}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#D4AF37]"
                />
              </div>

              {error && (
                <div data-testid="donate-error" className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 shrink-0" /> {error}
                </div>
              )}

              {/* Security Note */}
              <div className="bg-gray-50 rounded-xl p-3 flex items-start gap-2 text-xs text-gray-500">
                <CreditCard className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
                <span>{t.secureNote}</span>
              </div>

              {/* Single donate button — Stripe handles worldwide payments */}
              <button
                type="button"
                onClick={() => startDonation('stripe')}
                disabled={loading || !selectedAmount}
                data-testid="donate-submit-btn"
                className="w-full bg-[#CD0000] text-white py-4 rounded-xl font-extrabold text-base hover:bg-[#c91560] transition-all disabled:opacity-50 flex items-center justify-center gap-2 animate-heartbeat"
              >
                <Heart className="w-5 h-5" />
                {loading ? t.processing : `${t.donate} — $${selectedAmount || 0} ${t.currency}`}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Donate;
